import React, { useState } from 'react'
import { Row, Col, Pagination, Tab, Tabs, Modal } from 'react-bootstrap'
import { Sliders, BarChart2 } from 'lucide-react'
import styled from 'styled-components'
import MiniClassDistributionCard from '../../MiniClassDistributionCard'
import { useExportRunning } from '../../../hooks/useExportRunning'
import { Project } from '../../../types/models/project'

interface ImageGalleryHeaderProps {
  project: Project | undefined
  imagesPerRow: number
  onImagesPerRowChange?: (size: number) => void
  showTagFilter?: boolean
  currentPage: number
  totalImages: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  activeTab?: string
  onTabSelect?: (tab: string | null) => void
  annoStats?: {
    statistics: {
      total_unannotated_images: number
      total_annotated_images: number
    }
  }
  onBulkExport?: () => void
}

const SizeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  background: #2a2a2a;
  border-radius: 8px;
  justify-self: end;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    color: #fff;
    font-size: 0.9rem;
  }
`

const RangeInput = styled.input`
  width: 150px;
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #2a2a2a;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  justify-self: center;
  z-index: 1;

  .pagination {
    margin: 0;
    position: relative;
    z-index: 1;
  }

  .page-link {
    background-color: #2a2a2a;
    border-color: #3a3a3a;
    color: #fff;
    padding: 0.35rem 0.6rem;
    transition: all 0.2s ease;
    font-size: 0.85rem;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    position: relative;
    z-index: 1;

    &:hover {
      background-color: #3a3a3a;
      border-color: #4a4a4a;
    }
  }

  .page-item.active .page-link {
    background-color: #0d6efd;
    border-color: #0d6efd;
    color: #fff;
  }

  .page-item.disabled .page-link {
    background-color: #222;
    border-color: #333;
    color: #666;
  }
`

const PaginationInfo = styled.div`
  color: #fff;
  font-size: 0.85rem;
  white-space: nowrap;
  padding: 0.35rem 0.75rem;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  font-weight: 500;
`

const TabsContainer = styled.div`
  .nav-tabs {
    border: none;
    gap: 0.5rem;
    margin: 0;
    display: flex;

    .nav-item {
      margin: 0;
    }

    .nav-link {
      border: none;
      background: #2a2a2a;
      color: #fff;
      border-radius: 8px;
      padding: 0.6rem 1rem;
      font-size: 0.9rem;
      transition: all 0.2s ease;
      margin: 0;

      &:hover {
        background: #3a3a3a;
      }

      &.active {
        background: #0d6efd;
        color: #fff;
      }

      .badge {
        background: rgba(255, 255, 255, 0.15);
        font-weight: 500;
        font-size: 0.75rem;
        padding: 0.25em 0.6em;
      }
    }
  }
`

const IconButton = styled.button`
  background: #2a2a2a;
  border: none;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  margin-right: 1rem;

  &:hover {
    background: #3a3a3a;
  }

  svg {
    color: #fff;
  }
`

const StyledModal = styled(Modal)`
  .modal-content {
    background: #1a1a1a;
    border: 1px solid #2a2a2a;
    border-radius: 12px;
    color: #fff;
  }

  .modal-header {
    background: #2a2a2a;
    border-bottom: 1px solid #3a3a3a;
    border-radius: 12px 12px 0 0;
    padding: 1rem;

    .modal-title {
      color: #fff;
      font-size: 1.1rem;
      font-weight: 500;
    }

    .btn-close {
      filter: invert(1) grayscale(100%) brightness(200%);
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    background: #1a1a1a;
    border-radius: 0 0 12px 12px;
    padding: 1.5rem;
  }
`

const ImageGalleryHeader: React.FC<ImageGalleryHeaderProps> = ({
  project,
  imagesPerRow,
  onImagesPerRowChange,
  currentPage,
  totalImages,
  itemsPerPage,
  onPageChange,
  activeTab,
  onTabSelect,
  annoStats,
  onBulkExport,
}) => {
  const exportRunning = useExportRunning()
  const [showDistributionModal, setShowDistributionModal] = useState(false)
  const totalPages = Math.ceil(totalImages / itemsPerPage)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalImages)

  const handleTabSelect = (tab: string | null) => {
    if (onTabSelect) onTabSelect(tab)
  }

  const renderTabTitle = (title: string, count: number) => (
    <>
      {title} <span className="badge ms-2">{count}</span>
    </>
  )

  return (
    <>
      <Row style={{ width: '100%', padding: '.5rem', paddingLeft: '1rem', paddingRight: '1rem' }}>
        {onTabSelect && (
          <Col>
            <TabsContainer>
              <Tabs activeKey={activeTab} onSelect={handleTabSelect}>
                <Tab
                  eventKey="content"
                  title={renderTabTitle(
                    'Content',
                    annoStats?.statistics.total_unannotated_images || 0
                  )}
                  tabClassName="unannotated-tab"
                />
                <Tab
                  eventKey="annotated"
                  title={renderTabTitle(
                    'Annotated',
                    annoStats?.statistics.total_annotated_images || 0
                  )}
                  tabClassName="annotated-tab"
                />
              </Tabs>
            </TabsContainer>
          </Col>
        )}
        <Col>
          <PaginationContainer>
            <Pagination>
              <Pagination.First onClick={() => onPageChange(1)} disabled={currentPage === 1} />
              <Pagination.Prev
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              <Pagination.Item active>{currentPage}</Pagination.Item>
              <Pagination.Next
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
            <PaginationInfo>
              {startItem}-{endItem} of {totalImages} images
            </PaginationInfo>
          </PaginationContainer>
        </Col>
        <Col
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            paddingRight: '0',
          }}
        >
          {onBulkExport &&
            activeTab === 'annotated' &&
            project?.annotation_type !== 'classification' && (
              <IconButton
                onClick={onBulkExport}
                style={{
                  marginRight: '0.5rem',
                  opacity: exportRunning ? 0.5 : 1,
                  pointerEvents: exportRunning ? ('none' as any) : 'auto',
                }}
              >
                <span className="text-white" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                  Send to Classification
                </span>
              </IconButton>
            )}
          <IconButton
            onClick={() => setShowDistributionModal(true)}
            style={{ backgroundColor: '#800085', width: '4rem' }}
          >
            <BarChart2 color="#000" size={24} />
          </IconButton>
          {onImagesPerRowChange && (
            <SizeControl>
              <label>
                <Sliders size={16} />
                Size
              </label>
              <RangeInput
                type="range"
                min="1"
                max="6"
                value={7 - imagesPerRow}
                onChange={(e) =>
                  onImagesPerRowChange && onImagesPerRowChange(7 - parseInt(e.target.value))
                }
                className="form-range"
              />
            </SizeControl>
          )}
        </Col>
      </Row>

      <StyledModal
        show={showDistributionModal}
        onHide={() => setShowDistributionModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Class Distribution</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MiniClassDistributionCard showButtons={false} />
        </Modal.Body>
      </StyledModal>
    </>
  )
}

export default ImageGalleryHeader
